/**
 * Application Entry Point
 *
 * This file initializes the React application and sets up performance monitoring.
 *
 * Key Features:
 * - Renders the main application and MFE components
 * - Initializes Datadog RUM only for cart/checkout pages to track checkout flow performance
 * - Automatic tracking is disabled (trackUserInteractions, trackResources, trackLongTasks)
 * - Custom action logs are used for performance monitoring
 *
 * Path Restrictions:
 * - Datadog RUM and checkout flow metrics are only initialized on:
 *   - /rent/cart
 *   - /rent/checkout
 * - All other pages skip Datadog initialization to reduce overhead
 */

import App from './App';
import ReactDOM from 'react-dom/client';
import reportWebVitals from './reportWebVitals';
import { datadogRum } from '@datadog/browser-rum';
import { updateCheckoutFlowMetric } from './utils/log-checkout-flow-metric';
import dayjs from 'dayjs';
import { DATADOG_ALLOWED_PATHS } from './constants/app';
import Main from './renderer/Main';

/* Render MFE application root element */
const MFE_ROOT_ID = 'rental-web-components-root';
const mfeElement = document.getElementById(MFE_ROOT_ID) as HTMLElement;
if (!mfeElement) {
  const newRootElement = document.createElement('div');
  newRootElement.id = MFE_ROOT_ID;
  document.body.appendChild(newRootElement);
  const root = ReactDOM.createRoot(newRootElement);
  root.render(<Main />);
}

/* Render App root element */
const rootElement = document.getElementById('esr-root') as HTMLElement;
if (rootElement) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(<App />);
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

const isProduction = process.env.REACT_APP_CI_COMMIT_REF_SLUG === 'main';
const appEnvironment = isProduction ? 'prod' : 'stage';
const appVersion = (process.env.APP_VERSION as string);

const currentUrl = window.location.href;
const isAllowedPath = DATADOG_ALLOWED_PATHS.some((pattern) => pattern.test(currentUrl));

// Only initialize Datadog in production on allowed paths
// Temp allow staging to initialize Datadog for testing
if (isProduction && isAllowedPath) {
  /**
   * Datadog RUM Configuration
   * - Automatic tracking disabled (user interactions, resources, long tasks)
   * - Only custom action logs are used for performance monitoring
   * - See log-checkout-flow-metric.ts for custom metric implementation
   */
  datadogRum.init({
    applicationId: "270d6432-4a68-4719-ab95-1120185e45d4",
    clientToken: "pub23a21296ed86636ddb8e54422ddbec8b",
    site: "datadoghq.com",
    service: "rental-web-components",
    env: appEnvironment,
    version: appVersion,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 0,
    trackUserInteractions: false,
    trackResources: false,
    trackLongTasks: false,
    defaultPrivacyLevel: "allow",
    allowedTracingUrls: [],
    enableExperimentalFeatures: ["feature_flags"],
  });

  /**
   * Initialize checkout flow metrics tracking
   * This marks the app mount time, which is used to calculate total checkout flow duration
   * Additional metrics are tracked in cartEpic.ts (shift loading, RPP loading, quote calculation, etc.)
   */
  updateCheckoutFlowMetric({ appMountedAt: dayjs().format('MMM DD HH:mm:ss.SSS') });
}
